import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridTable, LoadingState } from "components";
import { useReport, useWmsItem } from "hooks";
import { S2dQuery, S2dRecord } from "types";
import SelectMulti from "components/ui-common/SelectMulti";
import { fmtNumber, formatCurrency } from "utils/helpers/accounting";

const today = new Date();
const formatDate = (d: Date) => d.toISOString().slice(0, 10);
const startOfYear = `${today.getFullYear()}-01-01`;
const todayString = formatDate(today);

const defaultFilters: S2dQuery = {
  from_date: startOfYear,
  to_date: todayString,
  product_ids: [],
};

const S2dReport: React.FC = () => {
  const { fetchS2dHkd } = useReport();
  const { fetchList: fetchProducts } = useWmsItem();
  const { data: products = [] } = fetchProducts(true);

  const productOptions = useMemo(
    () =>
      (products as any[])?.map?.((p: any) => ({
        value: String(p.id),
        label: `${p.code} - ${p.name}`,
      })) ?? [],
    [products],
  );

  const [filters, setFilters] = useState<S2dQuery>(defaultFilters);
  const [submitted, setSubmitted] = useState<S2dQuery | null>(defaultFilters);

  useEffect(() => {
    fetchS2dHkd.mutate(defaultFilters);
  }, [fetchS2dHkd.mutate]);

  const handleFilterChange = useCallback((key: keyof S2dQuery, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSubmit = useCallback(
    (event?: React.FormEvent) => {
      event?.preventDefault();
      if (!filters.from_date || !filters.to_date) {
        return;
      }
      setSubmitted(filters);
      fetchS2dHkd.mutate(filters);
    },
    [fetchS2dHkd, filters],
  );

  const records = (fetchS2dHkd.data?.data ?? []) as S2dRecord[];

  const totals = useMemo(
    () =>
      records.reduce(
        (acc, item) => {
          acc.opening_amount += Number(item.opening_amount ?? 0) || 0;
          acc.import_amount += Number(item.import_amount ?? 0) || 0;
          acc.export_amount += Number(item.export_amount ?? 0) || 0;
          acc.closing_amount += Number(item.closing_amount ?? 0) || 0;
          return acc;
        },
        {
          opening_amount: 0,
          import_amount: 0,
          export_amount: 0,
          closing_amount: 0,
        },
      ),
    [records],
  );

  const columns = useMemo(
    () => [
      {
        headerName: "Diễn giải (Tên hàng)",
        field: "product_name",
        minWidth: 200,
        flex: 1,
        pinned: "left",
      },
      {
        headerName: "Đơn vị tính",
        field: "unit",
        pinned: "left",
        minWidth: 90,
        maxWidth: 110,
      },
      {
        headerName: "Nhập",
        children: [
          {
            headerName: "Số lượng",
            field: "import_qty",
            minWidth: 100,
            valueFormatter: (params: any) => fmtNumber(params.value),
            cellClass: "text-right text-green-700",
          },
          {
            headerName: "Thành tiền",
            field: "import_amount",
            minWidth: 130,
            valueFormatter: (params: any) => formatCurrency(params.value),
            cellClass: "text-right font-semibold text-green-700",
          },
        ],
      },
      {
        headerName: "Xuất",
        children: [
          {
            headerName: "Số lượng",
            field: "export_qty",
            minWidth: 100,
            valueFormatter: (params: any) => fmtNumber(params.value),
            cellClass: "text-right text-red-700",
          },
          {
            headerName: "Thành tiền",
            field: "export_amount",
            minWidth: 130,
            valueFormatter: (params: any) => formatCurrency(params.value),
            cellClass: "text-right font-semibold text-red-700",
          },
        ],
      },
      {
        headerName: "Tồn",
        children: [
          {
            headerName: "Số lượng",
            field: "closing_qty",
            minWidth: 100,
            valueFormatter: (params: any) => fmtNumber(params.value),
            cellClass: "text-right font-bold",
          },
          {
            headerName: "Thành tiền",
            field: "closing_amount",
            minWidth: 130,
            valueFormatter: (params: any) => formatCurrency(params.value),
            cellClass: "text-right font-bold text-blue-700",
          },
        ],
      },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      minWidth: 100,
    }),
    [],
  );

  const isLoading = fetchS2dHkd.isPending;

  return (
    <div className="space-y-6">
      {isLoading && (
        <LoadingState
          message="Đang tải báo cáo"
          description="Vui lòng chờ trong giây lát."
        />
      )}

      {/* TT152 Form Header */}
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm p-6">
        <div className="flex justify-between items-start mb-4">
          <div className="text-sm text-gray-600 space-y-1">
            <p className="font-semibold">HỘ, CÁ NHÂN KINH DOANH: ..........</p>
            <p>Địa chỉ: ............................</p>
            <p>Mã số thuế: ..........................</p>
          </div>
          <div className="text-right text-sm text-gray-500 italic">
            <p className="font-semibold not-italic text-gray-700">
              Mẫu số S2d-HKD
            </p>
            <p>(Kèm theo Thông tư số 152/2025/TT-BTC</p>
            <p>ngày 31 tháng 12 năm 2025 của Bộ trưởng</p>
            <p>Bộ Tài chính)</p>
          </div>
        </div>
        <div className="text-center mb-4">
          <h2 className="text-lg font-bold text-gray-900 uppercase">
            Sổ chi tiết vật liệu, dụng cụ, sản phẩm, hàng hóa
          </h2>
          <p className="text-sm text-gray-600">
            Tên vật liệu, dụng cụ, sản phẩm, hàng hóa:
            ..........................
          </p>
          <p className="text-sm text-gray-600">
            Kỳ kê khai:{" "}
            {submitted
              ? `${submitted.from_date} → ${submitted.to_date}`
              : "..............."}
          </p>
        </div>
      </div>

      {/* Filter Form */}
      <form
        onSubmit={handleSubmit}
        className="rounded-lg border border-gray-200 bg-white shadow-sm p-4 space-y-4"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Từ ngày *
            </label>
            <input
              type="date"
              value={filters.from_date}
              onChange={(e) => handleFilterChange("from_date", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Đến ngày *
            </label>
            <input
              type="date"
              value={filters.to_date}
              onChange={(e) => handleFilterChange("to_date", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              required
            />
          </div>
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Lọc theo hàng hóa (tùy chọn)
          </label>
          <SelectMulti
            isMulti
            isClearable
            options={productOptions}
            value={productOptions.filter((opt) =>
              (filters.product_ids ?? []).map(String).includes(opt.value),
            )}
            placeholder="Chọn hàng hóa - Để trống để xem tất cả"
            onChange={(vals: any) =>
              handleFilterChange(
                "product_ids",
                Array.isArray(vals) ? vals.map((v: any) => v.value) : [],
              )
            }
            className="react-select-container"
            classNamePrefix="react-select"
          />
        </div>
        <div className="flex items-center justify-end gap-2">
          <button
            type="submit"
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700"
            disabled={isLoading}
          >
            {isLoading ? "Đang tải..." : "Xem báo cáo"}
          </button>
        </div>
      </form>

      {/* Results */}
      {submitted && (
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm p-4 space-y-4">
          <p className="text-right text-sm text-gray-600 italic">
            Đơn vị tính: đồng
          </p>

          {/* Summary */}
          <div className="flex flex-wrap items-center gap-6 px-2 py-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="text-center">
              <p className="text-xs text-gray-500">Tồn đầu kỳ</p>
              <p className="text-base font-semibold text-gray-900">
                {formatCurrency(totals.opening_amount)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500">Nhập trong kỳ</p>
              <p className="text-base font-semibold text-green-700">
                {formatCurrency(totals.import_amount)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500">Xuất trong kỳ</p>
              <p className="text-base font-semibold text-red-700">
                {formatCurrency(totals.export_amount)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500">Tồn cuối kỳ</p>
              <p className="text-base font-semibold text-blue-700">
                {formatCurrency(totals.closing_amount)}
              </p>
            </div>
          </div>

          <AgGridTable
            rows={records}
            columns={columns as any}
            defaultColDef={defaultColDef}
            pagination={false}
            autoHeight
          />
        </div>
      )}
    </div>
  );
};

export default S2dReport;
